import type * as React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { TextButton } from '@xing-com/button';
import { XING_JOBS_SEARCH_KI_PATH } from '@xing-com/crate-core-routes';
import { IconSearch } from '@xing-com/icons';
import {
  mediaTopBarHidden,
  zIndexElevatedLayer5,
} from '@xing-com/layout-tokens';
import { SearchDropdownItem } from '@xing-com/platform-search-dropdown';
import { PopOver, usePopOver } from '@xing-com/pop-over';
import { spaceS, spaceM, spaceL } from '@xing-com/tokens';

import { ActionSlot } from '../action-slot';

export type SearchDropdownProps = {
  icon?: typeof IconSearch;
  size?: React.ComponentProps<typeof TextButton>['size'];
  type?: string;
  showLabel?: boolean;
  title?: string;
};

const listMinWidth = '252px';

const SearchPopOver = styled(PopOver)`
  display: none;
  min-width: ${listMinWidth};
  right: 0;
  z-index: ${zIndexElevatedLayer5};
  && {
    z-index: ${zIndexElevatedLayer5};
    padding: ${spaceM} ${spaceL};
  }
`;

const SearchDropdownContainer = styled.div`
  position: relative;
  &:hover ${SearchPopOver} {
    display: block;
  }
`;

export const SearchButton = styled(TextButton)<{ $type?: string }>`
  margin-left: ${spaceS};
  @media ${mediaTopBarHidden} {
    display: ${({ $type }) =>
      $type === 'subheaderSearchResults' ? 'inline' : 'none'};
  }
`;

export const SearchDropdown: React.FC<SearchDropdownProps> = ({
  icon,
  showLabel,
}) => {
  const { formatMessage } = useIntl();

  const popOver = usePopOver();

  return (
    <SearchDropdownContainer
      onMouseEnter={popOver.handleShow}
      onMouseLeave={popOver.handleHide}
    >
      <ActionSlot
        size="small"
        showLabel={showLabel}
        icon={icon}
        data-testid="search-dropdown-button"
        innerRef={popOver.triggerRef}
        aria-label={formatMessage({
          id: 'FRAME_HEADER_SEARCH',
        })}
        text={formatMessage({
          id: 'FRAME_HEADER_SEARCH',
        })}
        onClick={popOver.togglePopOver}
      />
      <SearchPopOver
        data-testid="search-dropdown-menu"
        isInFlow
        onOutsideClick={popOver.handleHide}
        show={popOver.show}
        triggerRef={popOver.triggerRef}
      >
        <>
          <SearchDropdownItem
            itemKey="members"
            icon={IconSearch}
            href="/search/members"
            title={formatMessage({ id: 'SEARCH_DROPDOWN_MEMBERS' })}
            tracking={{ nwt_nav: 'search_member_search' }}
          />
          <SearchDropdownItem
            itemKey="jobs"
            icon={IconSearch}
            href={XING_JOBS_SEARCH_KI_PATH}
            title={formatMessage({ id: 'SEARCH_DROPDOWN_JOBS' })}
            tracking={{ nwt_nav: 'search_job_search' }}
          />
          <SearchDropdownItem
            itemKey="companies"
            icon={IconSearch}
            href="/search/companies"
            title={formatMessage({ id: 'SEARCH_DROPDOWN_COMPANIES' })}
            tracking={{ nwt_nav: 'search_companies_search' }}
          />
        </>
      </SearchPopOver>
    </SearchDropdownContainer>
  );
};
